import React, { useState, useEffect } from 'react';
import './DashboardOverview.css';

/**
 * Dashboard Overview Component for Principal
 * Shows statistics and recent pending items
 */
function DashboardOverviewPrincipal({ apiBase, stats, onRefresh, roleColor }) {
  const [recentPending, setRecentPending] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRecentPending();
  }, [apiBase]);

  const loadRecentPending = async () => {
    try {
      setLoading(true);
      
      // Fetch recent pending items (content + submissions combined)
      const contentResponse = await fetch(`${apiBase}/approver/pending-content?limit=5`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      
      const submissionsResponse = await fetch(`${apiBase}/approver/pending-submissions?limit=5`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const contentData = await contentResponse.json();
      const submissionsData = await submissionsResponse.json();

      // Combine and sort by date
      const items = [];
      
      if (contentData.ok && Array.isArray(contentData.data)) {
        items.push(...contentData.data.map(item => ({
          ...item,
          type: 'content',
          label: 'Content Change'
        })));
      }

      if (submissionsData.ok && Array.isArray(submissionsData.data)) {
        items.push(...submissionsData.data.map(item => ({
          ...item,
          type: 'submission',
          label: 'Submission'
        })));
      }

      items.sort((a, b) => new Date(b.created_at || b.submitted_at) - new Date(a.created_at || a.submitted_at));
      setRecentPending(items.slice(0, 5));
    } catch (err) {
      console.error('Error loading recent pending:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('id-ID', { month: 'short', day: 'numeric' });
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'content':
        return '#3b82f6';
      case 'submission':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  return (
    <div className="dashboard-overview-principal">
      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card pending-content" style={{ '--accent-color': roleColor }}>
          <div className="stat-number" style={{ color: roleColor }}>
            {stats.pendingContent}
          </div>
          <div className="stat-label">Konten Menunggu</div>
          <div className="stat-description">Content changes awaiting approval</div>
        </div>

        <div className="stat-card pending-submissions" style={{ '--accent-color': roleColor }}>
          <div className="stat-number" style={{ color: roleColor }}>
            {stats.pendingSubmissions}
          </div>
          <div className="stat-label">Pengajuan Menunggu</div>
          <div className="stat-description">Submissions awaiting review</div>
        </div>

        <div className="stat-card approved-month" style={{ '--accent-color': roleColor }}>
          <div className="stat-number" style={{ color: roleColor }}>
            {stats.approvedThisMonth}
          </div>
          <div className="stat-label">Disetujui Bulan Ini</div>
          <div className="stat-description">Items approved this month</div>
        </div>

        <div className="stat-card rejected-month" style={{ '--accent-color': roleColor }}>
          <div className="stat-number" style={{ color: roleColor }}>
            {stats.rejectedThisMonth}
          </div>
          <div className="stat-label">Ditolak Bulan Ini</div>
          <div className="stat-description">Items rejected this month</div>
        </div>
      </div>

      {/* Recent Pending Items */}
      <div className="recent-section">
        <div className="section-header">
          <h3>Menunggu Tindakan</h3>
          <button 
            className="refresh-btn"
            onClick={() => {
              loadRecentPending();
              onRefresh();
            }}
            title="Refresh"
            style={{ color: roleColor }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36 9 9 0 0110.64 10.63" />
              <path d="M20.49 15a9 9 0 01-14.85 3.36 9 9 0 01-10.64-10.63" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : recentPending.length > 0 ? (
          <div className="recent-list">
            {recentPending.map((item, idx) => (
              <div key={idx} className="recent-item">
                <div className="item-type-badge" style={{ backgroundColor: getTypeColor(item.type) }}>
                  {item.type === 'content' ? 'Content' : 'Submission'}
                </div>
                <div className="item-content">
                  <div className="item-title">{item.title || item.name || 'Untitled'}</div>
                  <div className="item-meta">
                    <span className="item-by">
                      {item.type === 'content' ? `By ${item.submitted_by || 'Unknown'}` : `From ${item.submitter_name || 'Unknown'}`}
                    </span>
                    <span className="item-date">{formatDate(item.created_at || item.submitted_at)}</span>
                  </div>
                </div>
                <div className="item-action">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>Tidak ada item yang menunggu</p>
            <small>All items have been reviewed</small>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardOverviewPrincipal;
